import { baseApi } from "../baseApi";

/**
 * Public Mock Test API.
 *
 * All endpoints use *optional* authentication: the shared `baseApi`
 * automatically attaches `Authorization: Bearer <token>` when a token exists
 * in localStorage, and omits it otherwise. The refresh-token retry flow in
 * `baseApi` only runs when the caller *had* a token, so guest requests that
 * receive a 401 are never retried as a logged-in request — they simply
 * continue as a guest, exactly as the contract requires.
 *
 * Nothing here writes per-answer state. In public mode the only write call is
 * `submitPublicMockTest`, made once at the end of the test.
 */
export const publicMockTestApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // GET /exams/public/books — books (collections) that contain at least one
    // guest-accessible exam. Inactive / empty books are already filtered out
    // server-side, so the FE renders whatever comes back.
    getPublicMockTestBooks: builder.query({
      query: () => ({ url: "/exams/public/books" }),
      transformResponse: (response) => response?.data ?? [],
      providesTags: ["PublicMockTest"],
    }),

    // GET /exams/public — list of public mock tests (cards).
    // Accepts an optional `book` param to filter by collection.
    getPublicMockTests: builder.query({
      query: (params = {}) => ({
        url: "/exams/public",
        params,
      }),
      transformResponse: (response) => ({
        items: response?.data ?? [],
        pagination: response?.pagination ?? null,
      }),
      providesTags: ["PublicMockTest"],
    }),

    // GET /exams/public/:examId — full test structure (no correct answers).
    // Pass `parts` (array of part numbers) to fetch only selected parts.
    getPublicMockTest: builder.query({
      query: ({ examId, parts }) => {
        const params = {};
        if (Array.isArray(parts) && parts.length > 0) {
          params.parts = parts.join(",");
        }
        return { url: `/exams/public/${examId}`, params };
      },
      transformResponse: (response) => response?.data ?? null,
      providesTags: (result, error, arg) => [
        { type: "PublicMockTest", id: arg?.examId },
      ],
    }),

    // POST /exams/public/:examId/submit — grade once at the end.
    submitPublicMockTest: builder.mutation({
      query: ({ examId, body }) => ({
        url: `/exams/public/${examId}/submit`,
        method: "POST",
        body,
      }),
      transformResponse: (response) => response?.data ?? null,
      // A new attempt may be persisted for logged-in users — refresh history.
      invalidatesTags: ["PublicMockTestHistory"],
    }),

    // GET /exams/public/history?type=summary|full|part — logged-in only.
    getPublicMockTestHistory: builder.query({
      query: (type = "summary") => ({
        url: "/exams/public/history",
        params: { type },
      }),
      transformResponse: (response) => response?.data ?? [],
      providesTags: ["PublicMockTestHistory"],
    }),

    // GET /exams/public/history/:attemptId — logged-in only. Full answer
    // review for a single persisted attempt: every question with the user's
    // selectedOption, the correctAnswer, per-question isCorrect and the
    // question content/choices, grouped by part/group.
    getPublicMockTestAttemptDetail: builder.query({
      query: (attemptId) => ({
        url: `/exams/public/history/${attemptId}`,
      }),
      transformResponse: (response) => response?.data ?? null,
      providesTags: (result, error, attemptId) => [
        { type: "PublicMockTestHistory", id: attemptId },
      ],
    }),
  }),
});

export const {
  useGetPublicMockTestBooksQuery,
  useGetPublicMockTestsQuery,
  useLazyGetPublicMockTestsQuery,
  useGetPublicMockTestQuery,
  useLazyGetPublicMockTestQuery,
  useSubmitPublicMockTestMutation,
  useGetPublicMockTestHistoryQuery,
  useLazyGetPublicMockTestHistoryQuery,
  useGetPublicMockTestAttemptDetailQuery,
  useLazyGetPublicMockTestAttemptDetailQuery,
} = publicMockTestApi;
